"use client";

import React from "react";
import { ShieldCheck, Sparkles, Gauge, Layers, ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, Flame } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const technicalPoints = [{
  id: "durability",
  title: "Durabilidade e aderência mecânica",
  description: "Distribuição homogénea de camadas que minimiza microfissuras e previne a degradação precoce de substratos metálicos e compósitos.",
  metric: "+45%",
  metricLabel: "resistência ao desgaste",
  icon: ShieldCheck,
  tag: "Durabilidade"
}, {
  id: "protection",
  title: "Proteção contra corrosão e humidade",
  description: "Atomização ultra-fina que sela superfícies complexas sem criar porosidades, bloqueando agentes químicos agressivos.",
  metric: "100%",
  metricLabel: "cobertura contínua",
  icon: Flame,
  tag: "Proteção"
}, {
  id: "performance",
  title: "Performance e precisão de caudal",
  description: "Controlo eletrónico da espessura com variação inferior a 2%, reduzindo o desperdício de matéria-prima e o retrabalho na linha.",
  metric: "<2%",
  metricLabel: "desvio de espessura",
  icon: Gauge,
  tag: "Performance"
}, {
  id: "uniformity",
  title: "Uniformidade em alta velocidade",
  description: "Sistemas automáticos que mantêm o padrão de pulverização constante mesmo com variações na velocidade do transportador.",
  metric: "0.5 bar",
  metricLabel: "estabilidade dinâmica",
  icon: Layers,
  tag: "Uniformidade"
}];
const keyFeatures = ["Tecnologia de pulverização de precisão Spraying Systems Co.", "Regulação automática de caudal através de modulação por largura de impulso (PWM)", "Bicos anti-entupimento com limpeza contínua e troca rápida", "Integração direta com autómatos industriais existentes (PLC/SCADA)"];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider text-secondary border-secondary/30 bg-secondary/5">
            Coberturas &amp; Revestimentos
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-6">
            Garantia de proteção uniforme e precisão de aplicação industrial
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            As soluções de engenharia da Eurocontrol eliminam imperfeições nas coberturas, reduzem o consumo de produto químico e asseguram a máxima longevidade dos materiais.
          </p>
        </div>

        {/* Benefits Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {technicalPoints.map((point, index) => {
          const Icon = point.icon;
          return <motion.div key={point.id} variants={itemVariants} data-index={index} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md hover:border-secondary/40 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-muted text-muted-foreground">
                          {point.tag}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {point.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {point.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-4 mt-auto border-t border-border/60">
                    <div className="text-2xl font-bold text-primary">
                      {point.metric}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase font-medium tracking-wide">
                      {point.metricLabel}
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Technical Specs Banner */}
        <div className="rounded-2xl bg-muted/50 border border-border p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-secondary font-semibold text-sm mb-3">
                <Sliders className="w-4 h-4" />
                <span>Eficiência de processo comprovada</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-4">
                Especificações que asseguram repetibilidade em ciclo contínuo
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                Combinamos a tecnologia líder mundial da Spraying Systems Co. com o apoio técnico local para dimensionar a solução exata para a sua linha de produção.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {keyFeatures.map((feat, i) => <div key={i} data-index={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-4 justify-center items-start lg:items-end">
              <div className="w-full sm:w-auto lg:w-full bg-card rounded-xl p-5 border border-border shadow-xs">
                <div className="text-xs uppercase font-medium text-muted-foreground mb-1">
                  Apoio à Engenharia
                </div>
                <div className="text-base font-semibold text-foreground mb-2">
                  Testes laboratoriais de pulverização
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Validação prévia de padrão de leque, tamanho de gota e impacto antes da instalação na fábrica.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="sm" className="w-full sm:w-auto">
                    <Link to="/ContactsPage">
                      Pedir consultoria técnica
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                    <Link to="/TechnicalSolutionsPage">
                      Ver soluções
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}